// Sprint 2026-Jul — VoltLedger Invoice Follow-up Queue.
// Operational workbench over APPROVED sales invoices for Accounts /
// Commercial / Recovery. Every figure comes from the shared receivables
// adapter (backend routes/invoice_queue.py) — no duplicated calculations.
// Row click opens the SAME Invoice Detail dynamic tab used by Retentions.
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/ui-helpers";
import { Card } from "@/components/ui/card";
import { MasterCrudList } from "@/components/MasterCrudList";
import { useOpenInvoiceDetail } from "@/pages/InvoiceReceivableDetail";
import {
  ChevronDown, ChevronLeft, ChevronRight, Copy, FileDown, FileUp, RotateCw, Search, Settings2, X,
} from "lucide-react";

const inr = (v) => (Number(v) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = "w-full border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";
const LIMIT = 25;

// Generic searchable filter dropdown — every queue filter (client, project,
// type, branch, FY, status) is type-to-search (Sep-2026 user request).
function SearchFilter({ testid, value, onChange, options, allLabel, width = "w-44", searchPlaceholder = "Search…" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const sel = options.find((o) => o.value === value);
  const s = q.trim().toLowerCase();
  const list = options.filter((o) => !s || (o.label || "").toLowerCase().includes(s)
    || (o.sub || "").toLowerCase().includes(s));
  const pick = (v) => { onChange(v); setOpen(false); setQ(""); };
  return (
    <div className="relative" ref={boxRef}>
      <button type="button" data-testid={testid} onClick={() => setOpen((o) => !o)}
        className={inputCls + ` ${width} text-left flex items-center justify-between gap-1`}>
        <span className={`truncate ${sel ? "" : "text-slate-500"}`} title={sel ? sel.label : allLabel}>{sel ? sel.label : allLabel}</span>
        <ChevronDown size={12} className="shrink-0 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-72 bg-white border border-slate-300 shadow-lg" data-testid={`${testid}-dropdown`}>
          <div className="p-1.5 border-b border-slate-100">
            <input autoFocus data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder} className={inputCls} />
          </div>
          <div className="max-h-64 overflow-y-auto">
            <button type="button" data-testid={`${testid}-opt-all`} onClick={() => pick("")}
              className={`w-full text-left px-2.5 py-1.5 text-xs hover:bg-orange-50 ${!value ? "bg-orange-50 font-bold" : ""}`}>
              {allLabel}
            </button>
            {list.map((o) => (
              <button key={o.value} type="button" data-testid={`${testid}-opt-${o.value}`}
                onClick={() => pick(o.value)} title={o.label}
                className={`w-full text-left px-2.5 py-1.5 text-xs hover:bg-orange-50 ${value === o.value ? "bg-orange-50 font-bold" : ""}`}>
                <div className="truncate">{o.label}</div>
                {o.sub && <div className="text-[10px] text-slate-400 truncate">{o.sub}</div>}
              </button>
            ))}
            {!list.length && <div className="px-2.5 py-3 text-xs text-slate-400">No match for your search.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

const VIEWS = [
  ["outstanding", "Outstanding"], ["all", "All Approved"], ["fully_paid", "Fully Paid"],
  ["credit_note", "Credit Note"], ["retention_pending", "Retention Pending"],
  ["due_today", "Due Today"], ["overdue", "Overdue"],
  ["ses_pending", "SES Pending"], ["payment_pending", "Payment Pending"],
];

const EMPTY = { view: "outstanding", client_id: "", project_id: "", project_type: "", branch_id: "", fy: "", status: "", q: "", sub_vendor: "", rc_no: "" };

export default function InvoiceQueue() {
  const location = useLocation();
  const { user, hasPerm } = useAuth();
  const canManage = user?.role === "admin" || hasPerm("invoice_queue", "manage_masters");
  const canBulk = user?.role === "admin" || hasPerm("invoice_queue", "update_status") || hasPerm("invoice_queue", "update_jobsheet");
  const openDetail = useOpenInvoiceDetail("/invoice-queue");

  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState({ rows: [], total: 0, totals: {}, view_counts: {} });
  const [masters, setMasters] = useState({ branches: [], clients: [], projects: [] });
  const [meta, setMeta] = useState({ status_options: [], fys: [] });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showMasters, setShowMasters] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const uploadRef = useRef(null);
  const appliedSearchRef = useRef("");

  const filterParams = (f) => {
    const params = { view: f.view };
    for (const k of ["client_id", "project_id", "project_type", "branch_id", "fy", "status", "q", "sub_vendor", "rc_no"]) {
      if (f[k]) params[k] = f[k];
    }
    return params;
  };

  async function downloadTemplate() {
    setBulkBusy(true);
    try {
      const r = await api.get("/invoice-queue/bulk-template", { params: filterParams(filters), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "invoice_queue_status_update_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded — fill New Status / New SES Date and upload back");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Template download failed");
    } finally { setBulkBusy(false); }
  }

  async function uploadExcel(fileObj) {
    if (!fileObj) return;
    setBulkBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const r = await api.post("/invoice-queue/bulk-update", fd);
      setBulkResult(r.data);
      load(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBulkBusy(false);
      if (uploadRef.current) uploadRef.current.value = "";
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const [b, c, p, m] = await Promise.all([
          api.get("/branches"), api.get("/clients"), api.get("/projects-picker"),
          api.get("/invoice-queue/meta"),
        ]);
        const arr = (r) => (Array.isArray(r.data) ? r.data : r.data?.items || []);
        setMasters((prev) => ({ ...prev, branches: arr(b), clients: arr(c), projects: arr(p) }));
        setMeta(m.data);
      } catch { /* optional */ }
    })();
    // Project Type filter — Work Types master from the Project library.
    api.get("/work-types")
      .then((r) => setMasters((m) => ({ ...m, workTypes: (r.data || []).map((w) => w.name).filter(Boolean) })))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load(p = page, f = filters) {
    setLoading(true);
    try {
      const params = { view: f.view, skip: p * LIMIT, limit: LIMIT };
      for (const k of ["client_id", "project_id", "project_type", "branch_id", "fy", "status", "q", "sub_vendor", "rc_no"]) {
        if (f[k]) params[k] = f[k];
      }
      const r = await api.get("/invoice-queue", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load invoice queue");
    } finally { setLoading(false); }
  }

  // Skip the default mount load when URL params exist — the drill-down
  // effect below performs the (filtered) initial load instead.
  useEffect(() => { if (!location.search) load(0, filters); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dashboard drill-down: /invoice-queue?status=X or ?view=overdue
  useEffect(() => {
    if (!location.pathname.startsWith("/invoice-queue") || !location.search) return;
    if (appliedSearchRef.current === location.search) return;
    appliedSearchRef.current = location.search;
    const sp = new URLSearchParams(location.search);
    const status = sp.get("status") || "";
    const client_id = sp.get("client_id") || "";
    const project_id = sp.get("project_id") || "";
    const view = sp.get("view") || ((status || client_id || project_id) ? "all" : "");
    if (!status && !view && !client_id && !project_id) return;
    const nf = { ...EMPTY, status, client_id, project_id, view: view || "outstanding" };
    setFilters(nf); setPage(0); load(0, nf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const switchView = (v) => { const nf = { ...filters, view: v }; setFilters(nf); setPage(0); load(0, nf); };
  const totals = data.totals || {};
  const counts = data.view_counts || {};
  const pages = Math.max(1, Math.ceil((data.total || 0) / LIMIT));

  return (
    <div data-testid="invoice-queue-page">
      <PageHeader
        eyebrow="Receivables"
        title="Invoice Follow-up Queue"
        subtitle="Every approved Sales Invoice, tracked through its collection lifecycle — SES, due dates, status follow-ups, retention and credit notes. Click any invoice to open its operational detail page."
        actions={(
          <div className="flex flex-wrap gap-2">
            <button data-testid="btn-excel-template" onClick={downloadTemplate} disabled={bulkBusy}
              className="border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50">
              <FileDown size={14} /> Excel Template
            </button>
            {canBulk && (
              <>
                <input ref={uploadRef} data-testid="bulk-upload-input" type="file" accept=".xlsx" className="hidden"
                  onChange={(e) => uploadExcel(e.target.files?.[0])} />
                <button data-testid="btn-excel-upload" onClick={() => uploadRef.current?.click()} disabled={bulkBusy}
                  className="border border-sky-300 bg-sky-50 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-sky-700 hover:bg-sky-100 flex items-center gap-2 disabled:opacity-50">
                  <FileUp size={14} /> {bulkBusy ? "Processing…" : "Upload Filled Excel"}
                </button>
              </>
            )}
            {canManage && (
              <button data-testid="btn-manage-masters" onClick={() => setShowMasters(true)}
                className="border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50 flex items-center gap-2">
                <Settings2 size={14} /> Manage Masters
              </button>
            )}
          </div>
        )}
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {[["Invoices in View", totals.invoices, false, "border-slate-300 bg-slate-100 text-slate-900", "iq-kpi-invoices"],
          ["Outstanding", totals.outstanding, true, "border-rose-200 bg-rose-50 text-rose-900", "iq-kpi-outstanding"],
          ["Retention Pending", totals.retention_pending, true, "border-amber-200 bg-amber-50 text-amber-900", "iq-kpi-retention"],
          [`Overdue (${totals.overdue_count || 0})`, totals.overdue_amount, true, "border-rose-300 bg-rose-100 text-rose-900", "iq-kpi-overdue"]].map(([l, v, money, tone, tid]) => (
          <div key={tid} className={`border px-3 py-2.5 ${tone}`} data-testid={tid}>
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{l}</div>
            <div className="text-sm font-bold mt-0.5">{money ? `₹${inr(v)}` : (v || 0)}</div>
          </div>
        ))}
      </div>

      <Card>
        {/* View pills */}
        <div className="px-4 pt-3 pb-2 flex flex-wrap gap-1.5 border-b border-slate-100">
          {VIEWS.map(([k, l]) => (
            <button key={k} data-testid={`iq-view-${k}`} onClick={() => switchView(k)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold border ${filters.view === k
                ? "border-orange-500 bg-orange-50 text-orange-700"
                : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
              {l}{counts[k] !== undefined && <span className="ml-1 opacity-60">({counts[k]})</span>}
            </button>
          ))}
        </div>

        {/* Filters — all type-to-search */}
        <div className="px-4 py-2.5 flex flex-wrap gap-2 items-center border-b border-slate-100">
          <SearchFilter testid="iq-filter-client" value={filters.client_id} onChange={(v) => setF("client_id", v)}
            allLabel="All Clients" width="w-44" searchPlaceholder="Search client…"
            options={masters.clients.map((c) => ({ value: c.id, label: c.name || c.client_name || "" }))} />
          <SearchFilter testid="iq-filter-project" value={filters.project_id} onChange={(v) => setF("project_id", v)}
            allLabel="All Projects" width="w-48" searchPlaceholder="Search project or client…"
            options={masters.projects.map((p) => ({ value: p.id, label: p.project_name || p.name || "", sub: p.client_name || "" }))} />
          <SearchFilter testid="iq-filter-project-type" value={filters.project_type} onChange={(v) => setF("project_type", v)}
            allLabel="All Project Types" width="w-40" searchPlaceholder="Search type…"
            options={(masters.workTypes || []).map((t) => ({ value: t, label: t }))} />
          <SearchFilter testid="iq-filter-branch" value={filters.branch_id} onChange={(v) => setF("branch_id", v)}
            allLabel="All Branches" width="w-36" searchPlaceholder="Search branch…"
            options={masters.branches.map((b) => ({ value: b.id, label: b.branch_name || b.name || "" }))} />
          <SearchFilter testid="iq-filter-fy" value={filters.fy} onChange={(v) => setF("fy", v)}
            allLabel="All FY" width="w-28" searchPlaceholder="Search FY…"
            options={(meta.fys || []).map((f) => ({ value: f, label: f }))} />
          <SearchFilter testid="iq-filter-status" value={filters.status} onChange={(v) => setF("status", v)}
            allLabel="All Statuses" width="w-52" searchPlaceholder="Search status…"
            options={[{ value: "__not_set__", label: "Not Set" },
              ...(meta.status_options || []).map((s) => ({ value: s.label, label: s.label }))]} />
          {/* Jun-2026 — Excel-like searchable column filters */}
          <SearchFilter testid="iq-filter-subvendor" value={filters.sub_vendor} onChange={(v) => setF("sub_vendor", v)}
            allLabel="All Subvendors" width="w-44" searchPlaceholder="Search subvendor…"
            options={[{ value: "__blank__", label: "(Blanks)" },
              ...(meta.sub_vendors || []).map((v) => ({ value: v, label: v }))]} />
          <SearchFilter testid="iq-filter-rcno" value={filters.rc_no} onChange={(v) => setF("rc_no", v)}
            allLabel="All RC Numbers" width="w-40" searchPlaceholder="Search RC number…"
            options={[{ value: "__blank__", label: "(Blanks)" },
              ...(meta.rc_numbers || []).map((v) => ({ value: v, label: v }))]} />
          <div className="relative">
            <Search size={12} className="absolute left-2 top-2.5 text-slate-400" />
            <input data-testid="iq-filter-q" value={filters.q} onChange={(e) => setF("q", e.target.value)}
              placeholder="Invoice / RC / Order no…" className={inputCls + " pl-6 w-44"} />
          </div>
          <button data-testid="iq-apply" onClick={() => { setPage(0); load(0); }}
            className="bg-slate-900 text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-slate-800">
            <RotateCw size={12} className={loading ? "animate-spin" : ""} /> Apply
          </button>
          <button data-testid="iq-reset" onClick={() => { setFilters(EMPTY); setPage(0); load(0, EMPTY); }}
            className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">Reset</button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="invoice-queue-table">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                {["Invoice", "RC No", "Subvendor", "Order No", "Client", "Project", "Branch", "Inv Date", "Service Period",
                  "Amount", "Net Payable", "Outstanding", "SES", "Due Date", "Overdue",
                  "Status", "Last Updated", "Retn Pending", "CN"].map((h) => (
                  <th key={h} className="px-2.5 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={19} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && data.rows.length === 0 && <tr><td colSpan={19} className="px-3 py-8 text-center text-slate-400">No invoices match this view / filters.</td></tr>}
              {!loading && data.rows.map((r) => (
                <tr key={r.invoice_id} data-testid={`iq-row-${r.invoice_id}`}
                  onClick={() => openDetail(r.invoice_id, r.invoice_number)}
                  className="border-b border-slate-50 hover:bg-orange-50/40 cursor-pointer">
                  <td className="px-2.5 py-2 font-semibold whitespace-nowrap text-sky-700">
                    <span className="inline-flex items-center gap-1">
                      {r.invoice_number}
                      {/* Jun-2026 — copy the invoice no. without opening the detail tab */}
                      <button data-testid={`iq-copy-inv-${r.invoice_id}`} title="Copy invoice number"
                        onClick={(e) => {
                          e.stopPropagation();
                          const txt = r.invoice_number || "";
                          const done = () => toast.success(`Copied ${txt}`);
                          if (navigator.clipboard?.writeText) navigator.clipboard.writeText(txt).then(done).catch(() => done());
                          else {
                            const ta = document.createElement("textarea");
                            ta.value = txt; document.body.appendChild(ta);
                            ta.select(); document.execCommand("copy"); ta.remove(); done();
                          }
                        }}
                        className="text-slate-300 hover:text-sky-700 p-0.5"><Copy size={12} /></button>
                    </span>
                  </td>
                  <td className="px-2.5 py-2 whitespace-nowrap max-w-[130px] truncate" title={r.rc_number} data-testid={`iq-rc-no-${r.invoice_id}`}>{r.rc_number || "—"}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap max-w-[150px] truncate" title={r.sub_vendor_name} data-testid={`iq-subvendor-${r.invoice_id}`}>{r.sub_vendor_name || "—"}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap max-w-[140px] truncate" title={r.order_no} data-testid={`iq-order-no-${r.invoice_id}`}>{r.order_no || "—"}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap max-w-[180px] truncate" title={r.client_name}>{r.client_name}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap max-w-[160px] truncate" title={r.project_name}>{r.project_name}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap">{r.branch_name || "—"}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap">{r.invoice_date}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap">{r.service_period || "—"}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">{inr(r.invoice_amount)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">{inr(r.net_payable)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums font-bold">{inr(r.outstanding)}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap">
                    <span className={`border px-1.5 py-0.5 text-[10px] font-bold ${r.ses_status === "SES Approved"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                      {r.ses_status === "SES Approved" ? (r.ses_date || "Approved") : "Pending"}
                    </span>
                  </td>
                  <td className="px-2.5 py-2 whitespace-nowrap">{r.due_date || <span className="text-rose-600 font-bold">Missing</span>}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap">
                    {r.days_overdue > 0 ? <span className="text-rose-700 font-bold">{r.days_overdue}d</span> : "—"}
                  </td>
                  <td className="px-2.5 py-2 whitespace-nowrap max-w-[190px] truncate" title={r.receivable_status}>
                    {r.receivable_status
                      ? <span className="border border-sky-200 bg-sky-50 text-sky-800 px-1.5 py-0.5 text-[10px] font-bold">{r.receivable_status}</span>
                      : <span className="text-slate-400">Not Set</span>}
                  </td>
                  <td className="px-2.5 py-2 whitespace-nowrap text-slate-500">
                    {r.status_updated_at ? `${r.status_updated_at.slice(0, 10)} · ${r.status_updated_by}` : "—"}
                  </td>
                  <td className="px-2.5 py-2 text-right tabular-nums text-amber-700">{r.pending_retention > 0 ? inr(r.pending_retention) : "—"}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap">{r.cn_total > 0 ? `₹${inr(r.cn_total)}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between text-xs">
          <div className="text-slate-500">{data.total || 0} invoice(s) · Page {page + 1} of {pages}</div>
          <div className="flex gap-1.5">
            <button disabled={page === 0} onClick={() => { const p = page - 1; setPage(p); load(p); }}
              className="px-2.5 py-1.5 border border-slate-300 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft size={13} /></button>
            <button disabled={page + 1 >= pages} onClick={() => { const p = page + 1; setPage(p); load(p); }}
              className="px-2.5 py-1.5 border border-slate-300 disabled:opacity-40 hover:bg-slate-50"><ChevronRight size={13} /></button>
          </div>
        </div>
      </Card>

      {showMasters && <ManageMastersDialog onClose={() => setShowMasters(false)} onChanged={() => {
        api.get("/invoice-queue/meta").then((r) => setMeta(r.data)).catch(() => {});
      }} />}
      {bulkResult && <BulkResultDialog result={bulkResult} onClose={() => setBulkResult(null)} />}
    </div>
  );
}

function BulkResultDialog({ result, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="bulk-result-dialog" onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-auto">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-bold text-slate-800">Excel Bulk Update — Result</div>
          <button data-testid="bulk-result-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            {[["Rows Processed", result.processed, "border-slate-300 bg-slate-100 text-slate-900"],
              ["Status Updated", result.status_updated, "border-sky-200 bg-sky-50 text-sky-900"],
              ["SES Updated", result.ses_updated, "border-emerald-200 bg-emerald-50 text-emerald-900"],
              ["Skipped / Errors", `${result.skipped} / ${result.error_count}`, result.error_count > 0 ? "border-rose-300 bg-rose-50 text-rose-900" : "border-slate-200 bg-slate-50 text-slate-600"]].map(([l, v, tone]) => (
              <div key={l} className={`border px-3 py-2.5 ${tone}`}>
                <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{l}</div>
                <div className="text-sm font-bold mt-0.5">{v}</div>
              </div>
            ))}
          </div>
          {(result.errors || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-rose-600 mb-1.5">Row Errors {result.error_count > result.errors.length ? `(first ${result.errors.length} of ${result.error_count})` : ""}</div>
              <div className="border border-rose-100 max-h-64 overflow-auto" data-testid="bulk-error-list">
                {result.errors.map((e, i) => (
                  <div key={i} className="px-3 py-1.5 border-b border-rose-50 text-xs flex gap-2">
                    <span className="text-slate-400 whitespace-nowrap">Row {e.row}</span>
                    <span className="font-semibold whitespace-nowrap">{e.invoice_no || "—"}</span>
                    <span className="text-rose-700">{e.error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(result.errors || []).length === 0 && (
            <div className="text-xs text-emerald-700">All rows processed cleanly. Status changes are recorded on each invoice's Status Timeline (source: bulk_excel); SES changes recalculated due dates automatically.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ManageMastersDialog({ onClose, onChanged }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="manage-masters-dialog" onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-3xl shadow-2xl border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-bold text-slate-800">Manage Masters — Invoice Status & Remarks</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-6">
          <MasterCrudList
            title="Invoice Status Master (Receivable Status Options)"
            testid="master-status"
            load={async () => (await api.get("/receivables/meta")).data.status_options || []}
            create={(label) => api.post("/receivable-status-options", { label })}
            update={(id, payload) => api.put(`/receivable-status-options/${id}`, payload)}
            remove={(id) => api.delete(`/receivable-status-options/${id}`)}
          />
          <MasterCrudList
            title="Status Remark Master (predefined follow-up remarks)"
            testid="master-remarks"
            load={async () => (await api.get("/receivable-status-remarks")).data.rows || []}
            create={(label) => api.post("/receivable-status-remarks", { label })}
            update={(id, payload) => api.put(`/receivable-status-remarks/${id}`, payload)}
            remove={(id) => api.delete(`/receivable-status-remarks/${id}`)}
          />
        </div>
        <div className="px-5 pb-4 text-[11px] text-slate-400">
          Status renames cascade to invoices automatically. Statuses in use cannot be deleted — deactivate them instead. Changes are reflected in the queue, invoice detail and dashboard immediately.
        </div>
        <div className="hidden">{onChanged && ""}</div>
      </div>
    </div>
  );
}
